/**
 * BabyKidsCheckout - Complete checkout flow for Baby & Kids Starter Kit bundles
 * Flow: Bundle Review → Delivery → Installation → Schedule → Payment
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, Truck, Wrench, Calendar as CalendarIcon, CreditCard,
  ChevronRight, ChevronLeft, CheckCircle, MapPin, Clock,
  Shield, AlertTriangle, Phone, Mail, User, Loader2,
  Baby, ShoppingBag, Home, CreditCard as CardIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Checkout steps
const STEPS = [
  { id: 1, name: 'Review', icon: ShoppingBag, description: 'Review bundle' },
  { id: 2, name: 'Delivery', icon: Truck, description: 'Delivery options' },
  { id: 3, name: 'Services', icon: Wrench, description: 'Installation' },
  { id: 4, name: 'Schedule', icon: CalendarIcon, description: 'Pick date' },
  { id: 5, name: 'Payment', icon: CreditCard, description: 'Pay' },
];

// Delivery options
const DELIVERY_OPTIONS = {
  UK: [
    { id: 'standard', name: 'Standard Delivery', description: '5-7 business days', price: 9.99 },
    { id: 'express', name: 'Express Delivery', description: '2-3 business days', price: 19.99 },
    { id: 'next_day', name: 'Next Day Delivery', description: 'Order before 2pm', price: 29.99 },
    { id: 'collect', name: 'Click & Collect', description: 'Free - collect from store', price: 0 },
  ],
  LK: [
    { id: 'standard', name: 'Standard Delivery', description: '7-10 business days', price: 500 },
    { id: 'express', name: 'Express Delivery', description: '3-5 business days', price: 1500 },
    { id: 'collect', name: 'Store Pickup', description: 'Free - Colombo stores', price: 0 },
  ],
};

// Installation services
const INSTALLATION_SERVICES = {
  UK: [
    { id: 'cot_assembly', name: 'Cot Assembly', description: 'Professional assembly of cot/crib', price: 35, recommended: true },
    { id: 'safety_check', name: 'Safety Installation', description: 'Gates, corner guards, socket covers', price: 45, recommended: true, safety: true },
    { id: 'furniture_assembly', name: 'Furniture Assembly', description: 'Wardrobes, changing tables', price: 50 },
    { id: 'car_seat_fitting', name: 'Car Seat Fitting', description: 'Professional ISOFIX installation', price: 25, recommended: true, safety: true },
    { id: 'nursery_setup', name: 'Full Nursery Setup', description: 'Complete room setup service', price: 150 },
  ],
  LK: [
    { id: 'cot_assembly', name: 'Cot Assembly', description: 'Professional assembly of cot/crib', price: 2500, recommended: true },
    { id: 'safety_check', name: 'Safety Installation', description: 'Gates, corner guards', price: 3000, recommended: true, safety: true },
    { id: 'furniture_assembly', name: 'Furniture Assembly', description: 'Wardrobes, changing tables', price: 3500 },
    { id: 'nursery_setup', name: 'Full Nursery Setup', description: 'Complete room setup service', price: 10000 },
  ],
};

// Time slots
const TIME_SLOTS = [
  { id: 'morning', label: 'Morning (9am - 12pm)', value: '09:00-12:00' },
  { id: 'afternoon', label: 'Afternoon (12pm - 4pm)', value: '12:00-16:00' },
  { id: 'evening', label: 'Evening (4pm - 7pm)', value: '16:00-19:00' },
];

// Step Progress Component
function StepProgress({ currentStep, steps }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${isActive ? 'text-pink-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-pink-100 border-2 border-pink-600' :
                  isComplete ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Bundle Review Step
function BundleReviewStep({ bundleData, onNext, currencySymbol }) {
  if (!bundleData || !bundleData.items || bundleData.items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Bundle Found</h3>
          <p className="text-gray-500 mb-4">Please build your starter kit first</p>
          <Button onClick={() => window.location.href = '/new-parent-starter-kit'}>
            Build Starter Kit
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Baby className="w-5 h-5 text-pink-600" />
          Review Your Bundle
        </CardTitle>
        <CardDescription>
          Confirm the items in your starter kit
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
          <p className="text-sm text-pink-800">
            <strong>Age Stage:</strong> {bundleData.ageStage?.replace('_', ' ').charAt(0).toUpperCase() + bundleData.ageStage?.replace('_', ' ').slice(1)}
          </p>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {bundleData.items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{item?.icon || '📦'}</span>
                <div>
                  <p className="font-medium">{item?.name || 'Item'}</p>
                  {item?.safety && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                      <Shield className="w-3 h-3 mr-1" /> Safety Item
                    </Badge>
                  )}
                </div>
              </div>
              <span className="font-medium">{currencySymbol}{item?.price || 0}</span>
            </div>
          ))}
        </div>

        <div className="p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Bundle Total ({bundleData.items.length} items)</span>
            <span className="text-2xl font-bold text-purple-700">
              {currencySymbol}{bundleData.totalPrice}
            </span>
          </div>
        </div>

        <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-600" onClick={onNext}>
          Continue to Delivery
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

// Delivery Step
function DeliveryStep({ country, deliveryData, onNext, onBack, currencySymbol }) {
  const [option, setOption] = useState(deliveryData?.option || '');
  const [address, setAddress] = useState(deliveryData?.address || '');
  const [postcode, setPostcode] = useState(deliveryData?.postcode || '');
  const [phone, setPhone] = useState(deliveryData?.phone || '');
  
  const options = DELIVERY_OPTIONS[country] || DELIVERY_OPTIONS.UK;
  const selectedOption = options.find(o => o.id === option);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-pink-600" />
          Delivery Options
        </CardTitle>
        <CardDescription>
          Choose how you'd like to receive your items
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={option} onValueChange={setOption}>
          {options.map(opt => (
            <label 
              key={opt.id}
              className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                option === opt.id ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value={opt.id} />
                <div>
                  <p className="font-medium">{opt.name}</p>
                  <p className="text-sm text-gray-500">{opt.description}</p>
                </div>
              </div>
              <span className="font-medium">
                {opt.price === 0 ? 'FREE' : `${currencySymbol}${opt.price}`}
              </span>
            </label>
          ))}
        </RadioGroup>

        {option && option !== 'collect' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-pink-600" />
              Delivery Address
            </h4>
            <div>
              <Label>Full Address *</Label>
              <Textarea 
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="House number, street name, city"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Postcode *</Label>
                <Input 
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder={country === 'UK' ? 'SW1A 1AA' : '10100'}
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={country === 'UK' ? '+44 7xxx xxxxxx' : '+94 7x xxx xxxx'}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
            onClick={() => onNext({
              option,
              price: selectedOption?.price || 0,
              address,
              postcode,
              phone
            })}
            disabled={!option || (option !== 'collect' && (!address || !postcode))}
          >
            Continue to Services
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Installation Services Step
function ServicesStep({ country, servicesData, onNext, onBack, currencySymbol }) {
  const [selectedServices, setSelectedServices] = useState(servicesData?.services || {});
  
  const services = INSTALLATION_SERVICES[country] || INSTALLATION_SERVICES.UK;
  
  const toggleService = (id) => {
    setSelectedServices(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  const totalPrice = services
    .filter(s => selectedServices[s.id])
    .reduce((sum, s) => sum + s.price, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-pink-600" />
          Installation Services
        </CardTitle>
        <CardDescription>
          Optional professional services to help you get set up
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {services.map(service => (
          <div 
            key={service.id}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              selectedServices[service.id] ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => toggleService(service.id)}
          >
            <div className="flex items-start gap-3">
              <Checkbox checked={selectedServices[service.id] || false} className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">{service.name}</p>
                  {service.recommended && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Recommended</Badge>
                  )}
                  {service.safety && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                      <Shield className="w-3 h-3 mr-1" /> Safety
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">{service.description}</p>
              </div>
              <span className="font-medium">{currencySymbol}{service.price}</span>
            </div>
          </div>
        ))}

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-700">
              Installation services are provided by vetted professionals. All safety installations are guidance only.
            </p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Services Total</span>
            <span className="text-lg font-bold">{currencySymbol}{totalPrice}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
            onClick={() => onNext({ services: selectedServices, price: totalPrice })}
          >
            Continue to Schedule
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Schedule Step
function ScheduleStep({ scheduleData, onNext, onBack }) {
  const [date, setDate] = useState(scheduleData?.date || null);
  const [timeSlot, setTimeSlot] = useState(scheduleData?.timeSlot || '');
  const [notes, setNotes] = useState(scheduleData?.notes || '');
  
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 3); // At least 3 days from now

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-pink-600" />
          Schedule Delivery
        </CardTitle>
        <CardDescription>
          Choose a convenient date and time
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="mb-2 block">Select Date</Label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < minDate}
              className="rounded-md border"
            />
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Preferred Time Slot</Label>
              <RadioGroup value={timeSlot} onValueChange={setTimeSlot}>
                {TIME_SLOTS.map(slot => (
                  <label 
                    key={slot.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      timeSlot === slot.id ? 'border-pink-500 bg-pink-50' : 'border-gray-200'
                    }`}
                  >
                    <RadioGroupItem value={slot.id} />
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>{slot.label}</span>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            
            <div>
              <Label>Special Instructions (optional)</Label>
              <Textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any access codes, parking info, or special requests..."
                rows={3}
              />
            </div>
          </div>
        </div>

        {date && (
          <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
            <p className="text-sm text-pink-800">
              <strong>Selected:</strong> {date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {timeSlot && ` • ${TIME_SLOTS.find(s => s.id === timeSlot)?.label}`}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
            onClick={() => onNext({ date, timeSlot, notes })}
            disabled={!date || !timeSlot}
          >
            Continue to Payment
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Payment Step
function PaymentStep({ bundleData, checkoutData, onConfirm, onBack, loading, currencySymbol }) {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState(checkoutData.delivery?.phone || '');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  const bundlePrice = bundleData?.totalPrice || 0;
  const deliveryPrice = checkoutData.delivery?.price || 0;
  const servicesPrice = checkoutData.services?.price || 0;
  const totalPrice = bundlePrice + deliveryPrice + servicesPrice;

  const handleSubmit = () => {
    if (!customerName || !customerEmail || !agreeTerms) {
      toast.error('Please fill in all required fields');
      return;
    }
    onConfirm({
      customerName,
      customerEmail,
      customerPhone,
      paymentMethod,
      totalPrice
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-pink-600" />
          Review & Pay
        </CardTitle>
        <CardDescription>
          Confirm your order and complete payment
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Order Summary */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <h4 className="font-semibold">Order Summary</h4>
          
          <div className="flex justify-between text-sm">
            <span>Starter Kit Bundle ({bundleData?.items?.length} items)</span>
            <span>{currencySymbol}{bundlePrice}</span>
          </div>
          
          {deliveryPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span>Delivery</span>
              <span>{currencySymbol}{deliveryPrice}</span>
            </div>
          )}
          
          {servicesPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span>Installation Services</span>
              <span>{currencySymbol}{servicesPrice}</span>
            </div>
          )}
          
          <div className="border-t pt-2 flex justify-between font-bold">
            <span>Total</span>
            <span className="text-lg text-pink-600">{currencySymbol}{totalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Schedule Summary */}
        {checkoutData.schedule?.date && (
          <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
            <p className="text-sm text-pink-800">
              <strong>Delivery Date:</strong> {checkoutData.schedule.date.toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric' })}
              {checkoutData.schedule.timeSlot && ` • ${TIME_SLOTS.find(s => s.id === checkoutData.schedule.timeSlot)?.label}`}
            </p>
          </div>
        )}

        {/* Customer Details */}
        <div className="space-y-4">
          <h4 className="font-semibold">Your Details</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input 
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input 
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="+44 7xxx xxxxxx"
            />
          </div>
        </div>

        {/* Payment Method */}
        <div className="space-y-3">
          <h4 className="font-semibold">Payment Method</h4>
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${paymentMethod === 'card' ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`}>
              <RadioGroupItem value="card" />
              <CardIcon className="w-5 h-5 text-gray-600" />
              <span>Credit/Debit Card</span>
            </label>
            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${paymentMethod === 'paypal' ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`}>
              <RadioGroupItem value="paypal" />
              <span className="text-blue-600 font-bold">PayPal</span>
            </label>
          </RadioGroup>
        </div>

        {/* Terms */}
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
          <Checkbox 
            checked={agreeTerms}
            onCheckedChange={setAgreeTerms}
            className="mt-1"
          />
          <p className="text-sm text-gray-600">
            I agree to the <a href="#" className="text-pink-600 underline">Terms & Conditions</a> and understand that installation services are guidance only and do not constitute official safety certification.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
            onClick={handleSubmit}
            disabled={!customerName || !customerEmail || !agreeTerms || loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            Pay {currencySymbol}{totalPrice.toFixed(2)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Success Step
function SuccessStep({ orderId, navigate }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
        <p className="text-gray-500 mb-4">Thank you for your order</p>
        <div className="p-4 bg-gray-50 rounded-lg inline-block mb-6">
          <p className="text-sm text-gray-500">Order Reference</p>
          <p className="text-xl font-mono font-bold">{orderId}</p>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          You will receive a confirmation email with your order details and tracking information.
        </p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
          <Button onClick={() => navigate('/my-bookings')}>
            View My Orders
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function BabyKidsCheckout() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  // Load bundle data from localStorage
  const [bundleData, setBundleData] = useState(null);
  
  useEffect(() => {
    const saved = localStorage.getItem('starterKitBundle');
    if (saved) {
      try {
        setBundleData(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse bundle data');
      }
    }
  }, []);
  
  const country = bundleData?.country || 'UK';
  const currencySymbol = country === 'LK' ? 'Rs.' : '£';
  
  // Checkout data state
  const [checkoutData, setCheckoutData] = useState({
    delivery: null,
    services: null,
    schedule: null,
    payment: null
  });
  
  // Handle order submission
  const handleConfirmOrder = async (paymentData) => {
    setLoading(true);
    
    try {
      const orderPayload = {
        bundle_age_stage: bundleData?.ageStage,
        bundle_items: bundleData?.items?.map(i => ({ id: i?.id, name: i?.name, price: i?.price })) || [],
        bundle_price: bundleData?.totalPrice || 0,
        customer_name: paymentData.customerName,
        customer_email: paymentData.customerEmail,
        customer_phone: paymentData.customerPhone,
        delivery_option: checkoutData.delivery?.option,
        delivery_address: checkoutData.delivery?.address,
        delivery_postcode: checkoutData.delivery?.postcode,
        delivery_price: checkoutData.delivery?.price || 0,
        services: Object.keys(checkoutData.services?.services || {}).filter(k => checkoutData.services?.services[k]),
        services_price: checkoutData.services?.price || 0,
        schedule_date: checkoutData.schedule?.date?.toISOString(),
        schedule_time: checkoutData.schedule?.timeSlot,
        schedule_notes: checkoutData.schedule?.notes,
        payment_method: paymentData.paymentMethod,
        total_price: paymentData.totalPrice,
        country: country
      };
      
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOrderId(data.order_id);
        setStep(6); // Success step
        localStorage.removeItem('starterKitBundle'); // Clear the bundle
        toast.success('Order placed successfully!');
      } else {
        toast.error(data.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Baby className="w-6 h-6" />
            Starter Kit Checkout
          </h1>
          <p className="text-pink-100">Complete your order</p>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {step < 6 && <StepProgress currentStep={step} steps={STEPS} />}
        
        {step === 1 && (
          <BundleReviewStep 
            bundleData={bundleData}
            currencySymbol={currencySymbol}
            onNext={() => setStep(2)}
          />
        )}
        
        {step === 2 && (
          <DeliveryStep 
            country={country}
            deliveryData={checkoutData.delivery}
            currencySymbol={currencySymbol}
            onNext={(data) => {
              setCheckoutData(prev => ({ ...prev, delivery: data }));
              setStep(3);
            }}
            onBack={() => setStep(1)}
          />
        )}
        
        {step === 3 && (
          <ServicesStep 
            country={country}
            servicesData={checkoutData.services}
            currencySymbol={currencySymbol}
            onNext={(data) => {
              setCheckoutData(prev => ({ ...prev, services: data }));
              setStep(4);
            }}
            onBack={() => setStep(2)}
          />
        )}
        
        {step === 4 && (
          <ScheduleStep 
            scheduleData={checkoutData.schedule}
            onNext={(data) => {
              setCheckoutData(prev => ({ ...prev, schedule: data }));
              setStep(5);
            }}
            onBack={() => setStep(3)}
          />
        )}
        
        {step === 5 && (
          <PaymentStep 
            bundleData={bundleData}
            checkoutData={checkoutData}
            currencySymbol={currencySymbol}
            loading={loading}
            onConfirm={handleConfirmOrder}
            onBack={() => setStep(4)}
          />
        )}
        
        {step === 6 && (
          <SuccessStep orderId={orderId} navigate={navigate} />
        )}
      </div>
    </div>
  );
}
